<template>
  <div class="signature-field-response">
    <div class="field-header">
      <h3 class="field-label">
        {{ field.label }}
        <span v-if="field.required" class="required-indicator">*</span>
      </h3>
    </div>

    <div v-if="field.description" class="field-description">
      {{ field.description }}
    </div>

    <!-- Mode Selector -->
    <v-btn-toggle
      v-if="!disabled"
      v-model="inputMode"
      mandatory
      dense
      class="mb-3"
      color="primary"
    >
      <v-btn small value="draw">
        <v-icon small left>mdi-draw</v-icon>
        Draw
      </v-btn>
      <v-btn small value="upload">
        <v-icon small left>mdi-upload</v-icon>
        Upload
      </v-btn>
    </v-btn-toggle>

    <!-- Existing Signature Preview - shown when signature exists and in draw mode -->
    <div v-if="hasValidSignature && inputMode === 'draw'" class="existing-signature-preview mb-3">
      <div class="signature-header" @click="showCurrentSignature = !showCurrentSignature">
        <span class="text-caption grey--text">Current Signature</span>
        <v-btn x-small text class="ml-2 toggle-btn">
          <v-icon x-small>{{ showCurrentSignature ? 'mdi-eye-off' : 'mdi-eye' }}</v-icon>
          {{ showCurrentSignature ? 'Hide' : 'Show' }}
        </v-btn>
      </div>
      <v-expand-transition>
        <div v-show="showCurrentSignature" class="signature-image-container">
          <img
            :src="signatureImageUrl"
            alt="Current signature"
            class="signature-preview-image"
          />
        </div>
      </v-expand-transition>
    </div>

    <!-- Draw Mode -->
    <div v-show="inputMode === 'draw'" class="signature-container" :class="{ 'disabled': disabled }">
      <canvas
        ref="signatureCanvas"
        class="signature-canvas"
        :class="{ 'has-signature': hasSignature && inputMode === 'draw' }"
        @mousedown="startDrawing"
        @mousemove="draw"
        @mouseup="stopDrawing"
        @mouseleave="stopDrawing"
        @touchstart.prevent="startDrawingTouch"
        @touchmove.prevent="drawTouch"
        @touchend="stopDrawing"
      ></canvas>

      <div v-if="!hasSignature && !disabled && inputMode === 'draw' && !hasValidSignature" class="signature-placeholder">
        {{ field.hint || 'Sign here' }}
      </div>

      <div class="signature-actions" v-if="!disabled">
        <v-btn
          x-small
          text
          color="error"
          @click="clearSignature"
          :disabled="!hasSignature && !hasValidSignature"
        >
          <v-icon x-small left>mdi-eraser</v-icon>
          Clear
        </v-btn>
      </div>
    </div>

    <!-- Upload Mode -->
    <div v-show="inputMode === 'upload'" class="upload-container" :class="{ 'disabled': disabled }">
      <div v-if="!hasValidSignature" class="upload-area" @click="triggerFileInput" @dragover.prevent @drop.prevent="handleDrop">
        <input
          ref="fileInput"
          type="file"
          accept="image/*"
          class="hidden-input"
          @change="handleFileSelect"
        />
        <v-icon large color="grey">mdi-cloud-upload</v-icon>
        <p class="upload-text">Click or drag image here</p>
        <p class="upload-hint">Supports JPG, PNG, GIF</p>
      </div>

      <div v-else class="uploaded-preview">
        <img :src="signatureImageUrl" alt="Uploaded signature" class="preview-image" />
        <v-btn
          v-if="!disabled"
          x-small
          text
          color="error"
          class="remove-btn"
          @click="removeUploadedImage"
        >
          <v-icon x-small left>mdi-delete</v-icon>
          Remove
        </v-btn>
      </div>
    </div>

    <div v-if="hasSignature || hasValidSignature" class="signature-info">
      <v-icon x-small color="success">mdi-check-circle</v-icon>
      <span class="ml-1">Signature captured</span>
    </div>

    <v-textarea
      v-model="localResponse.comment"
      outlined
      dense
      :readonly="disabled"
      label="Comment (Optional)"
      placeholder="Add any additional comments here..."
      rows="2"
      auto-grow
      @change="updateResponse"
    ></v-textarea>
  </div>
</template>

<script>
export default {
  name: 'SignatureFieldResponse',
  props: {
    field: {
      type: Object,
      required: true
    },
    disabled: {
      type: Boolean,
      default: false
    },
    response: {
      type: Object,
      required: false
    }
  },
  data() {
    return {
      localResponse: {
        fieldId: 0,
        signatureBase64: null,
        signatureMode: 'draw',
        comment: ''
      },
      inputMode: 'draw',
      isDrawing: false,
      hasSignature: false,
      uploadedImage: null,
      ctx: null,
      lastX: 0,
      lastY: 0,
      showCurrentSignature: true
    }
  },
  watch: {
    response: {
      handler(newVal) {
        if (newVal) {
          this.$nextTick(() => {
            this.initializeResponse();
          });
        }
      },
      immediate: true,
      deep: true
    },
    inputMode(newMode) {
      if (newMode === 'draw') {
        this.$nextTick(() => {
          this.initializeCanvas();
          // Load existing signature into canvas if available
          if (this.localResponse.signatureBase64) {
            const signatureData = this.ensureDataUrlPrefix(this.localResponse.signatureBase64);
            this.loadSignatureFromData(signatureData);
          }
        });
      } else if (newMode === 'upload') {
        // If switching to upload mode with existing signature, show it
        if (this.localResponse.signatureBase64 && !this.uploadedImage) {
          this.uploadedImage = this.ensureDataUrlPrefix(this.localResponse.signatureBase64);
          this.hasSignature = true;
        }
      }
    }
  },
  mounted() {
    this.initializeCanvas();
    // Check again after a delay to handle async response loading
    setTimeout(() => {
      if (this.response && this.response.signatureBase64 && !this.hasSignature) {
        console.log('SignatureField: Re-initializing with delayed response', this.response);
        this.initializeResponse();
      }
    }, 500);
  },
  computed: {
    /**
     * Check if there's valid signature data (not null, not string "null")
     */
    hasValidSignature() {
      const sig = this.response?.signatureBase64 || this.localResponse?.signatureBase64 || this.uploadedImage;
      // Check it's not null, not undefined, not empty, not the string "null"
      return sig && sig !== 'null' && sig !== 'undefined' && sig.length > 10;
    },

    /**
     * Get the signature image URL with proper data prefix
     */
    signatureImageUrl() {
      // Priority: uploadedImage > localResponse > response prop
      let sig = this.uploadedImage || this.localResponse?.signatureBase64 || this.response?.signatureBase64;

      // Validate signature data
      if (!sig || sig === 'null' || sig === 'undefined' || sig.length < 10) {
        return null;
      }

      // Add data URL prefix if missing
      if (sig.startsWith('data:')) {
        return sig;
      }
      return `data:image/png;base64,${sig}`;
    },

    /**
     * Get the signature URL directly from props for display
     * This ensures the signature is displayed even if local state isn't updated
     */
    displaySignatureUrl() {
      return this.signatureImageUrl;
    }
  },
  methods: {
    initializeCanvas() {
      const canvas = this.$refs.signatureCanvas;
      if (!canvas) return;

      const container = canvas.parentElement;
      canvas.width = container.clientWidth - 2;
      canvas.height = 150;

      this.ctx = canvas.getContext('2d');
      this.ctx.strokeStyle = '#000';
      this.ctx.lineWidth = 2;
      this.ctx.lineCap = 'round';
      this.ctx.lineJoin = 'round';
    },

    initializeResponse() {
      console.log('SignatureField initializeResponse called', {
        hasResponse: !!this.response,
        signatureBase64: this.response?.signatureBase64 ? 'exists' : 'null',
        signatureMode: this.response?.signatureMode
      });

      if (this.response) {
        this.localResponse = { ...this.response };

        if (this.response.signatureBase64) {
          // Ensure the base64 has the proper data URL prefix
          const signatureData = this.ensureDataUrlPrefix(this.response.signatureBase64);
          this.localResponse.signatureBase64 = signatureData;

          console.log('SignatureField: Has signature data, setting upload mode');

          // Always use upload mode for displaying existing signatures (most reliable)
          this.inputMode = 'upload';
          this.uploadedImage = signatureData;
          this.hasSignature = true;
          this.localResponse.signatureMode = 'upload';

          console.log('SignatureField: State after init', {
            inputMode: this.inputMode,
            hasSignature: this.hasSignature,
            uploadedImageExists: !!this.uploadedImage
          });
        } else {
          // No signature yet - use draw mode by default
          if (this.response.signatureMode) {
            this.inputMode = this.response.signatureMode;
          }
        }
      } else {
        this.localResponse = {
          fieldId: this.field.id || 0,
          signatureBase64: null,
          signatureMode: 'draw',
          comment: ''
        };
      }
    },

    /**
     * Ensure the base64 string has the proper data URL prefix
     */
    ensureDataUrlPrefix(base64String) {
      if (!base64String) return null;
      // Already has data URL prefix
      if (base64String.startsWith('data:')) {
        return base64String;
      }
      // Add PNG prefix by default (most common for signatures)
      return `data:image/png;base64,${base64String}`;
    },

    loadSignatureFromData(dataUrl) {
      if (!dataUrl) return;

      // If canvas context not ready, wait and retry
      if (!this.ctx) {
        this.$nextTick(() => {
          this.initializeCanvas();
          if (this.ctx) {
            this.loadSignatureFromData(dataUrl);
          } else {
            // Canvas still not ready - fallback to showing as uploaded image
            this.uploadedImage = dataUrl;
            this.inputMode = 'upload';
            this.hasSignature = true;
          }
        });
        return;
      }

      const img = new Image();
      img.onload = () => {
        const canvas = this.$refs.signatureCanvas;
        if (canvas && this.ctx) {
          this.ctx.clearRect(0, 0, canvas.width, canvas.height);
          this.ctx.drawImage(img, 0, 0);
          this.hasSignature = true;
        }
      };
      img.onerror = () => {
        // If loading into canvas fails, try showing as uploaded image
        console.warn('Failed to load signature into canvas, displaying as image');
        this.uploadedImage = dataUrl;
        this.inputMode = 'upload';
        this.hasSignature = true;
      };
      img.src = dataUrl;
    },

    getCanvasCoordinates(e) {
      const canvas = this.$refs.signatureCanvas;
      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;

      return {
        x: (e.clientX - rect.left) * scaleX,
        y: (e.clientY - rect.top) * scaleY
      };
    },

    getTouchCoordinates(e) {
      const canvas = this.$refs.signatureCanvas;
      const rect = canvas.getBoundingClientRect();
      const touch = e.touches[0];
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;

      return {
        x: (touch.clientX - rect.left) * scaleX,
        y: (touch.clientY - rect.top) * scaleY
      };
    },

    startDrawing(e) {
      if (this.disabled) return;

      this.isDrawing = true;
      const coords = this.getCanvasCoordinates(e);
      this.lastX = coords.x;
      this.lastY = coords.y;
    },

    startDrawingTouch(e) {
      if (this.disabled) return;

      this.isDrawing = true;
      const coords = this.getTouchCoordinates(e);
      this.lastX = coords.x;
      this.lastY = coords.y;
    },

    draw(e) {
      if (!this.isDrawing || this.disabled) return;

      const coords = this.getCanvasCoordinates(e);
      this.drawLine(coords.x, coords.y);
    },

    drawTouch(e) {
      if (!this.isDrawing || this.disabled) return;

      const coords = this.getTouchCoordinates(e);
      this.drawLine(coords.x, coords.y);
    },

    drawLine(x, y) {
      this.ctx.beginPath();
      this.ctx.moveTo(this.lastX, this.lastY);
      this.ctx.lineTo(x, y);
      this.ctx.stroke();

      this.lastX = x;
      this.lastY = y;
      this.hasSignature = true;
    },

    stopDrawing() {
      if (this.isDrawing) {
        this.isDrawing = false;
        this.saveSignature();
      }
    },

    saveSignature() {
      const canvas = this.$refs.signatureCanvas;
      if (!canvas) return;

      this.localResponse.signatureBase64 = canvas.toDataURL('image/png');
      this.localResponse.signatureMode = 'draw';
      this.updateResponse();
    },

    clearSignature() {
      const canvas = this.$refs.signatureCanvas;
      if (!canvas || !this.ctx) return;

      this.ctx.clearRect(0, 0, canvas.width, canvas.height);
      this.hasSignature = false;
      this.localResponse.signatureBase64 = null;
      this.updateResponse();
    },

    // Upload methods
    triggerFileInput() {
      if (this.disabled) return;
      this.$refs.fileInput.click();
    },

    handleFileSelect(e) {
      const file = e.target.files[0];
      if (file) {
        this.processFile(file);
      }
    },

    handleDrop(e) {
      if (this.disabled) return;
      const file = e.dataTransfer.files[0];
      if (file && file.type.startsWith('image/')) {
        this.processFile(file);
      }
    },

    processFile(file) {
      if (!file.type.startsWith('image/')) {
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        this.uploadedImage = e.target.result;
        this.hasSignature = true;
        this.localResponse.signatureBase64 = e.target.result;
        this.localResponse.signatureMode = 'upload';
        this.updateResponse();
      };
      reader.readAsDataURL(file);
    },

    removeUploadedImage() {
      this.uploadedImage = null;
      this.hasSignature = false;
      this.localResponse.signatureBase64 = null;
      this.$refs.fileInput.value = '';
      this.updateResponse();
    },

    updateResponse() {
      this.$emit('update:response', this.localResponse);
    }
  }
}
</script>

<style scoped>
.signature-field-response {
  width: 100%;
}

.field-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
}

.field-label {
  font-size: 1rem;
  font-weight: 500;
  color: #333;
  margin: 0 0 4px 0;
}

.required-indicator {
  color: #f44336;
  margin-left: 4px;
}

.field-description {
  font-size: 0.85rem;
  color: rgba(0, 0, 0, 0.6);
  margin-bottom: 12px;
}

.signature-container {
  position: relative;
  border: 2px dashed #ccc;
  border-radius: 8px;
  background: #fafafa;
  margin-bottom: 12px;
  overflow: hidden;
}

.signature-container.disabled {
  opacity: 0.7;
  pointer-events: none;
}

.signature-canvas {
  display: block;
  width: 100%;
  height: 150px;
  cursor: crosshair;
  background: transparent;
}

.signature-canvas.has-signature {
  border-color: #4caf50;
}

.signature-placeholder {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: #999;
  font-style: italic;
  pointer-events: none;
}

.signature-actions {
  position: absolute;
  top: 4px;
  right: 4px;
}

.signature-info {
  display: flex;
  align-items: center;
  font-size: 0.8rem;
  color: #4caf50;
  margin-bottom: 12px;
}

/* Upload styles */
.upload-container {
  margin-bottom: 12px;
}

.upload-container.disabled {
  opacity: 0.7;
  pointer-events: none;
}

.upload-area {
  border: 2px dashed #ccc;
  border-radius: 8px;
  background: #fafafa;
  padding: 24px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
}

.upload-area:hover {
  border-color: #1976d2;
  background: #f0f7ff;
}

.hidden-input {
  display: none;
}

.upload-text {
  margin: 8px 0 4px;
  color: #666;
  font-size: 0.9rem;
}

.upload-hint {
  margin: 0;
  color: #999;
  font-size: 0.8rem;
}

.uploaded-preview {
  position: relative;
  border: 2px solid #4caf50;
  border-radius: 8px;
  padding: 8px;
  background: #fafafa;
  text-align: center;
}

.preview-image {
  max-width: 100%;
  max-height: 150px;
  object-fit: contain;
}

.remove-btn {
  position: absolute;
  top: 4px;
  right: 4px;
}

/* Existing signature preview (shown when signature exists) */
.existing-signature-preview {
  border: 2px solid #4caf50;
  border-radius: 8px;
  padding: 8px;
  background: #f5f5f5;
}

.signature-header {
  display: flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
}

.signature-header:hover {
  opacity: 0.8;
}

.toggle-btn {
  text-transform: none !important;
  font-size: 11px !important;
}

.signature-image-container {
  text-align: center;
  padding-top: 8px;
}

.signature-preview-image {
  max-width: 100%;
  max-height: 120px;
  object-fit: contain;
}
</style>
